import { DOM_ANIMATIONS_SUPPORTED } from "./dom_backend.js";

export const SURFACE_EASE_OUT = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";
export const RASTER_PRIME_OPACITY = "0.002";

export interface SurfaceAppearMotion
{
  from_scale: number;
  overshoot_scale?: number;
  final_scale?: number;
  scale_up_ms: number;
  settle_ms?: number;
}

export interface SurfaceCloseMotion
{
  to_scale: number;
  duration_ms: number;
}

export function surfaceAnimationsSupported( node: HTMLElement | null | undefined ): boolean
{
  return !!node && DOM_ANIMATIONS_SUPPORTED && typeof node.animate === "function";
}

export function primeScaleFor( motion: SurfaceAppearMotion ): number
{
  return Math.max( motion.from_scale, motion.overshoot_scale ?? 1, motion.final_scale ?? 1 );
}

export function primeNodeForRaster( node: HTMLElement, scale: number ): void
{
  node.style.opacity = RASTER_PRIME_OPACITY;
  node.style.scale = scale === 1 ? "" : `${scale} ${scale}`;
}

export function unprimeNode( node: HTMLElement ): void
{
  if ( node.style.opacity === RASTER_PRIME_OPACITY )
    node.style.opacity = "";
  node.style.scale = "";
}

export function waitForNextFrame(): Promise<void>
{
  return new Promise<void>( resolve => requestAnimationFrame( () => resolve() ) );
}

export function waitForPresentedFrame(): Promise<void>
{
  return new Promise<void>( resolve =>
    requestAnimationFrame( () => requestAnimationFrame( () => resolve() ) ) );
}

export function runSurfaceAnimation(
  node: HTMLElement,
  keyframes: Keyframe[],
  duration_ms: number,
  cancel_on_finish: boolean
): Animation | null
{
  if ( !surfaceAnimationsSupported( node ) )
    return null;

  let anim: Animation;
  try
  {
    anim = node.animate( keyframes, { duration: duration_ms, fill: "both", composite: "replace" } );
  }
  catch ( e )
  {
    console.warn( "[surface_motion] animate() failed", e );
    return null;
  }

  if ( cancel_on_finish )
    anim.finished.then( () => anim.cancel() ).catch( () => {} );
  return anim;
}

export function appearKeyframes( motion: SurfaceAppearMotion ): { keyframes: Keyframe[]; duration_ms: number }
{
  const overshoot = motion.overshoot_scale ?? motion.final_scale ?? 1;
  const final_scale = motion.final_scale ?? 1;
  const settle = motion.settle_ms ?? 0;
  const total = motion.scale_up_ms + settle;

  if ( settle <= 0 )
  {
    return {
      duration_ms: total,
      keyframes: [
        { scale: `${motion.from_scale} ${motion.from_scale}`, opacity: 0, easing: SURFACE_EASE_OUT },
        { scale: `${final_scale} ${final_scale}`, opacity: 1 },
      ],
    };
  }

  return {
    duration_ms: total,
    keyframes: [
      { scale: `${motion.from_scale} ${motion.from_scale}`, opacity: 0, offset: 0, easing: SURFACE_EASE_OUT },
      { scale: `${overshoot} ${overshoot}`, opacity: 1, offset: motion.scale_up_ms / total, easing: SURFACE_EASE_OUT },
      { scale: `${final_scale} ${final_scale}`, opacity: 1, offset: 1 },
    ],
  };
}

export function closeKeyframes( node: HTMLElement, motion: SurfaceCloseMotion ): Keyframe[]
{
  const computed = getComputedStyle( node );
  const from_opacity = Number( computed.opacity );
  const from_scale = computed.scale && computed.scale !== "none" ? computed.scale : "1 1";
  return [
    { scale: from_scale, opacity: Number.isFinite( from_opacity ) ? from_opacity : 1, easing: SURFACE_EASE_OUT },
    { scale: `${motion.to_scale} ${motion.to_scale}`, opacity: 0 },
  ];
}

export function fadeBackdrop( backdrop: HTMLElement, to_opacity: number, duration_ms: number ): Animation | null
{
  if ( !surfaceAnimationsSupported( backdrop ) )
  {
    backdrop.style.opacity = `${to_opacity}`;
    return null;
  }

  const running = backdrop.getAnimations();
  const from_opacity = running.length > 0
    ? Number( getComputedStyle( backdrop ).opacity )
    : Number( backdrop.style.opacity === "" ? 1 : backdrop.style.opacity );
  for ( const anim of running )
    anim.cancel();

  backdrop.style.opacity = `${to_opacity}`;
  if ( !Number.isFinite( from_opacity ) || from_opacity === to_opacity )
    return null;

  let anim: Animation;
  try
  {
    anim = backdrop.animate(
      [ { opacity: from_opacity }, { opacity: to_opacity } ],
      { duration: duration_ms, fill: "none", easing: "ease-out" }
    );
  }
  catch
  {
    return null;
  }
  return anim;
}

export function snapBackdrop( backdrop: HTMLElement, opacity: number ): void
{
  if ( typeof backdrop.getAnimations === "function" )
  {
    for ( const running of backdrop.getAnimations() )
      running.cancel();
  }
  backdrop.style.opacity = `${opacity}`;
}
